import { useEffect, useRef } from 'react';
import { api } from './client';
import { useAuth } from './useAuth';

// Pushes location to BFF on app load and periodically when tab is visible.
// Honors the user's location_sharing preference; if 'off' it does nothing.
export function useLocationTracker() {
  const { friend } = useAuth();
  const lastPush = useRef(0);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!friend) return;
    const sharing = (friend as any).locationSharing ?? 'off';
    if (sharing === 'off') return;

    let cancelled = false;

    async function push(opts: { force?: boolean } = {}) {
      const now = Date.now();
      if (!opts.force && now - lastPush.current < 60_000) return;
      lastPush.current = now;

      // Try GPS first if precise
      if (sharing === 'precise' && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async pos => {
            if (cancelled) return;
            try {
              await api.pushLocation({
                lat: pos.coords.latitude, lng: pos.coords.longitude,
                accuracyM: pos.coords.accuracy, source: 'gps',
              });
            } catch {}
          },
          async () => {
            // Fallback to IP-based (server reads CF headers)
            try { await api.pushLocation({ source: 'ip' }); } catch {}
          },
          { enableHighAccuracy: false, maximumAge: 5 * 60_000, timeout: 8000 },
        );
      } else {
        // City-only mode: server resolves from CF IP geolocation
        try { await api.pushLocation({ source: 'ip' }); } catch {}
      }
    }

    push({ force: true });
    intervalRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') push();
    }, 5 * 60_000);
    const onVis = () => { if (document.visibilityState === 'visible') push(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [friend?.id, (friend as any)?.locationSharing]);
}
